import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Users, Car, CreditCard, Ban, Scale, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  const lastUpdated = "January 18, 2026";
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Terms of Service</h1>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-pink-100 rounded-lg">
                <FileText className="h-6 w-6 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Agreement to Terms</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") govern your use of the Tekstar mobile application and related services 
              (collectively, the "Service") operated by Tekstar ("we," "us," or "our"). By accessing or using our Service, 
              you agree to be bound by these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Car className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Service Description</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Tekstar provides a technology platform that connects:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Riders:</strong> Users seeking transportation services</li>
              <li><strong>Drivers:</strong> Independent contractors providing transportation services</li>
              <li><strong>Package Senders:</strong> Users sending packages</li>
              <li><strong>Bikers:</strong> Independent contractors providing delivery services</li>
            </ul>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <p className="text-gray-700">
                  <strong>Important:</strong> Tekstar is a technology platform only. We do not provide transportation 
                  services directly. Drivers and bikers are independent contractors, not employees of Tekstar.
                </p>
              </div>
            </div>
          </section>

          {/* User Accounts */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">User Accounts</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Registration</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You must be at least 18 years old to use our Service</li>
              <li>You must provide accurate, complete, and current information</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You are responsible for all activities under your account</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Driver Requirements</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Valid driver's license issued in Zambia</li>
              <li>Vehicle registration and insurance documents</li>
              <li>Pass background verification check</li>
              <li>Vehicle must meet safety standards</li>
              <li>Maintain required insurance coverage</li>
            </ul>
          </section>

          {/* Payments */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Payments & Fees</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Ride Fares</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Fares are calculated based on distance, time, and ride type</li>
              <li>Estimated fares are shown before booking confirmation</li>
              <li>Final fares may differ due to route changes, traffic, or waiting time</li>
              <li>Surge pricing may apply during high-demand periods</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Payment Methods</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Cash:</strong> Pay driver directly at the end of the ride</li>
              <li><strong>Wallet:</strong> Pre-loaded balance for cashless payments</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Cancellation Policy</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Free cancellation within 2 minutes of booking</li>
              <li>Cancellation fee may apply after driver is en route</li>
              <li>No-show fees apply if driver waits more than 5 minutes at pickup</li>
            </ul>
          </section>

          {/* Prohibited Activities */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Prohibited Activities</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">Users may not:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use the Service for any illegal purpose</li>
              <li>Transport illegal items or contraband</li>
              <li>Harass, threaten, or discriminate against other users</li>
              <li>Create fake accounts or impersonate others</li>
              <li>Interfere with the proper functioning of the Service</li>
              <li>Attempt to circumvent payment obligations</li>
              <li>Share account credentials with others</li>
              <li>Use the Service while under the influence of drugs or alcohol (drivers)</li>
              <li>Carry weapons in vehicles</li>
            </ul>
          </section>

          {/* Safety */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Safety</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are committed to the safety of all users. By using our Service, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Wear seatbelts during all rides</li>
              <li>Follow all applicable traffic laws</li>
              <li>Report any safety concerns or incidents immediately</li>
              <li>Not distract drivers during rides</li>
              <li>Allow ride tracking for safety purposes</li>
            </ul>
          </section>

          {/* Liability */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Scale className="h-6 w-6 text-gray-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Limitation of Liability</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Tekstar provides the Service "as is" without warranties</li>
              <li>We are not liable for actions of drivers, bikers, or other users</li>
              <li>We are not responsible for delays, route issues, or service interruptions</li>
              <li>Our total liability shall not exceed the amount paid by you in the past 12 months</li>
              <li>We are not liable for indirect, incidental, or consequential damages</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Clock className="h-6 w-6 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Termination</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may terminate or suspend your account immediately, without prior notice, for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Safety concerns</li>
              <li>Extended inactivity</li>
              <li>Request from law enforcement</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You may terminate your account at any time by contacting support. Wallet balances will be 
              refunded within 30 days minus any outstanding obligations.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the Republic of Zambia, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service 
              shall be resolved in the courts of Zambia.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of significant changes 
              via the app or email. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Email:</strong> legal@tekstar.co.zm<br />
              <strong>Address:</strong> Kasama, Northern Province, Zambia
            </p>
          </section>

          {/* Footer */}
          <div className="text-center pt-6 border-t">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Tekstar. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
